/* RESPONSIBLE TEAM: team-workflows */
import Step, { IsCustomerFacing } from 'embercom/models/operator/visual-builder/step';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import { buildValidations, validator } from 'ember-cp-validations';
import { type SyncHasMany } from '@ember-data/model';
import { type Block } from 'embercom/models/common/blocks/block';
import type Store from '@ember-data/store';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { LOCAL_VARIABLE_IDENTIFIER_PREFIX } from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import type Localization from '../../localizable-step/localization';
import { inject as service } from '@ember/service';
import type WorkflowLocalizationService from 'embercom/services/workflow-localization-service';

export const chatMessageStepBlockValidators = {
  blocks: [
    validator('blocks-not-empty', {
      message: 'operator.workflows.visual-builder.validations.blank-message-error',
      messageTranslated: true,
    }),
    validator('workflow-step-blocks-messenger-apps-are-valid'),
    validator('workflow-step-blocks-messenger-cards-are-valid'),
    validator('workflow-step-blocks-audio-snippets-have-no-meaningful-content', {
      message: 'operator.workflows.visual-builder.validations.audio-files-invalid',
      messageTranslated: true,
    }),
    validator('standalone/block-fields-available-in-production', {
      isWarning: true,
      lazy: false,
      messageKey: 'standalone.common.field-unavailable-in-production.warning-message',
    }),
  ],
};

const Validations = buildValidations({
  ...chatMessageStepBlockValidators,
});

export default class ChatMessage extends Step.extend(Validations) {
  @service declare workflowLocalizationService: WorkflowLocalizationService;

  @fragmentArray('common/blocks/block', { polymorphic: true, typeKey: 'modelKey' })
  declare blocks: SyncHasMany<Block>;

  @fragmentArray('operator/localizable-step/localization')
  declare localizations: SyncHasMany<Localization>;

  @computed('blocks')
  get localVariableIdentifiersUsed() {
    let templateVariables = extractTemplateVariables(this.blocks.toArray());
    return templateVariables.filter((variable) =>
      variable.startsWith(LOCAL_VARIABLE_IDENTIFIER_PREFIX),
    );
  }

  get localization() {
    return this.localizations.find(
      (localization) => localization.locale === this.workflowLocalizationService.selectedLocaleId,
    );
  }

  get localBlocks() {
    return this.localization?.blocks || this.blocks || [];
  }

  get supportedChannels() {
    return SUPPORTED_CHANNELS.chatMessage;
  }

  static get supportedContexts() {
    return [TargetContext.Conversation, TargetContext.CustomerTicket];
  }

  static get requiredBillingFeature() {
    return 'bot_workflows';
  }

  static get canBeCustomerFacing() {
    return IsCustomerFacing.Yes;
  }

  get jsonBlocks() {
    return this.localBlocks.serialize();
  }

  get serializedBlocks() {
    // We only need to serialize the blocks if they are a FragmentArray (in tests they are already serialized)
    if (this.localBlocks && this.localBlocks.type === 'common/blocks/block') {
      return this.localBlocks.serialize();
    } else {
      return this.localBlocks;
    }
  }

  static createNewStep(store: Store): ChatMessage {
    let blocks = [
      store.createFragment('common/blocks/paragraph', {
        type: 'paragraph',
        text: '', // let the component decide what the default or placeholder text should be
      }),
    ];

    return store.createRecord('operator/visual-builder/step/chat-message', {
      blocks,
      type: 'operator/visual-builder/step/chat-message',
    });
  }
}
